import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, Link, useParams } from 'react-router-dom';
import { useKeycloak } from '@react-keycloak/web';
import { useNotifications } from '../contexts/NotificationContext';
import {
    Breadcrumb,
    Form,
    Input,
    Select,
    Upload,
    Button,
    message,
    Modal,
    Spin,
    Alert,
    Space,
    Switch,
    Dropdown,
} from 'antd';
import {
    InboxOutlined,
    CloudOutlined,
    HomeOutlined,
    PlusOutlined,
    KeyOutlined,
    DatabaseOutlined,
    ApiOutlined,
    CopyOutlined,
    MoreOutlined,
    DeleteOutlined,
} from '@ant-design/icons';
import {
    getModelFams,
    registerOllamaChatModel,
    createAssistant,
    getAssistantStatus,
    getGroups,
    getDataCollections,
    getCloudCatalogProviders,
    getCloudCatalogModels,
    getUserCloudProviders,
    createCloudProvider,
    getPlatformSettings,
    fetchAssistant,
    getAssistants,
    getAssistantTeam,
    saveAssistantTeam,
    updateAssistant,
    duplicateAssistant,
    uploadAssistantAvatar,
    uploadCollectionFileLegacy,
    createDataCollection,
    getCurrentUserRoles,
    getUsers,
    listModules,
    getAssistantModules,
    updateAssistantModules,
} from '../services/api';
import AssistantToolsSection from '../components/tools/AssistantToolsSection';
import AssistantTeamSection from '../components/assistants/AssistantTeamSection';
import './DataCollections.css';
import './AddAssistant.css';
import PromptEditor from '../components/prompt/PromptEditor';
import LocalOllamaModelField from '../components/models/LocalOllamaModelField';
import CatalogChatModelField from '../components/models/CatalogChatModelField';
import CreateCollectionModal from '../components/collections/CreateCollectionModal';
import SettingsField, { SettingsBlock, SettingsPageHead, SettingsPanel } from '../components/settings/SettingsField';
import SettingsSegment from '../components/settings/SettingsSegment';
import AssistantOwnerSelect from '../components/assistants/AssistantOwnerSelect';
import { mediaUrl } from '../utils/mediaUrl';

/** Draft schema v1 — bump `v` when changing serialized shape for forward compatibility. */
const ADD_ASSISTANT_DRAFT_KEY = 'focusml:add-assistant-draft:v1';

function normalizeConversationStarters(value) {
  if (!Array.isArray(value)) return [];
  const starters = [];
  value.forEach((item) => {
    const text = String(item || '').replace(/\s+/g, ' ').trim();
    if (!text || starters.length >= 8) return;
    starters.push(text.slice(0, 200));
  });
  return starters;
}

function buildChatSettings(values, routing) {
  const settings = {
    default_collection_attached: values.default_collection_attached !== false,
    default_tools_enabled: Boolean(values.default_tools_enabled),
    default_rag_strategy: values.default_rag_strategy === 'agent_led' ? 'agent_led' : 'legacy',
    conversation_starters: normalizeConversationStarters(values.conversation_starters),
  };
  if (routing) {
    settings.router_mode = routing.routerMode === true;
    settings.router_contextualize = routing.contextualize !== false;
  }
  return settings;
}
const { Option } = Select;

const SETTINGS_CHAPTERS = [
  { id: 'general', label: 'General' },
  { id: 'model', label: 'Model' },
  { id: 'knowledge', label: 'Knowledge' },
  { id: 'capabilities', label: 'Capabilities' },
  { id: 'access', label: 'Access' },
];

const chapterForField = (name) => {
  const key = Array.isArray(name) ? name[0] : name;
  if (key === 'name' || key === 'description' || key === 'system_prompt') return 'general';
  if (key === 'executionType' || key === 'model') return 'model';
  if (
    key === 'data_collection_id'
    || key === 'dataFile'
    ||     key === 'default_collection_attached'
    || key === 'default_tools_enabled'
    || key === 'default_rag_strategy'
    || key === 'conversation_starters'
  ) return 'knowledge';
  return 'access';
};

const executionTypeOptions = [
  {
    key: 'on-premise',
    title: 'Host Locally',
    description: 'Full control, data privacy, and offline availability.',
    icon: <HomeOutlined />,
    iconClass: 'add-assistant-host-icon-local',
  },
  {
    key: 'cloud',
    title: 'Cloud Provider',
    description: 'Cloud models from 100+ providers via LiteLLM.',
    icon: <CloudOutlined />,
    iconClass: 'add-assistant-host-icon-cloud',
  },
];

const AddModel = () => {
  const { assistantId } = useParams();
  const isEditMode = Boolean(assistantId);
  const { keycloak, initialized } = useKeycloak();
  const navigate = useNavigate();
  const { addNotification, updateNotification } = useNotifications();
  const [modelFamilies, setModelFamilies] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [duplicating, setDuplicating] = useState(false);
  const [modelsLoading, setModelsLoading] = useState(false);
  const loadedCloudRef = useRef({ provider: null, model: null, providerId: null });
  const [form] = Form.useForm();
  const watchedAssistantName = Form.useWatch('name', form);
  const [selectedModel, setSelectedModel] = useState(null);
  const [selectedExecutionType, setSelectedExecutionType] = useState(null);
  const [fileList, setFileList] = useState([]);
  const [groups, setGroups] = useState([]);
  const [selectedVisibility, setSelectedVisibility] = useState('private');
  const [dataCollections, setDataCollections] = useState([]);
  const [dataSourceMode, setDataSourceMode] = useState('none');
  const [numericAssistantId, setNumericAssistantId] = useState(null);
  const [routerMode, setRouterMode] = useState(false);
  const [routerContextualize, setRouterContextualize] = useState(true);
  const [teamMembers, setTeamMembers] = useState([]);
  const [teamCandidates, setTeamCandidates] = useState([]);
  // Cloud state
  const [catalogProviders, setCatalogProviders] = useState([]);
  const [catalogModels, setCatalogModels] = useState([]);
  const [catalogModelsLoading, setCatalogModelsLoading] = useState(false);
  const [userCloudProviders, setUserCloudProviders] = useState([]);
  const [selectedCloudProvider, setSelectedCloudProvider] = useState(null); // provider object from catalog
  const [selectedCloudProviderId, setSelectedCloudProviderId] = useState(null); // user's API key config ID
  const [showAddProviderModal, setShowAddProviderModal] = useState(false);
  const [addProviderForm] = Form.useForm();
  const [addingProvider, setAddingProvider] = useState(false);
  const [cloudWarningAccepted, setCloudWarningAccepted] = useState(false);
  const [modelSearch, setModelSearch] = useState('');
  const [cloudModelsEnabled, setCloudModelsEnabled] = useState(true);
  const [editLoadDone, setEditLoadDone] = useState(false);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreviewUrl, setAvatarPreviewUrl] = useState(null);
  const [platformAvatarMaxKb, setPlatformAvatarMaxKb] = useState(512);
  const [createCollectionOpen, setCreateCollectionOpen] = useState(false);
  const [userOptions, setUserOptions] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loadedOwnerId, setLoadedOwnerId] = useState(null);
  const [draftPromptDone, setDraftPromptDone] = useState(false);
  const draftSaveTimerRef = useRef(null);
  const [moduleDefinitions, setModuleDefinitions] = useState([]);
  const [moduleConfigs, setModuleConfigs] = useState({});
  const [settingsChapter, setSettingsChapter] = useState('general');

  const saveDraftToStorage = useCallback(() => {
    if (isEditMode) return;
    try {
      localStorage.setItem(
        ADD_ASSISTANT_DRAFT_KEY,
        JSON.stringify({
          v: 1,
          savedAt: Date.now(),
          values: form.getFieldsValue(),
          dataSourceMode,
          selectedExecutionType,
          selectedModel,
          selectedCloudProviderId,
          cloudWarningAccepted,
          cloudProviderLitellm: selectedCloudProvider?.litellm_provider ?? null,
          cloudModelName: selectedExecutionType === 'cloud' ? (selectedModel || '').trim() || null : null,
        }),
      );
    } catch (_) {
      /* storage unavailable */
    }
  }, [
    isEditMode,
    form,
    dataSourceMode,
    selectedExecutionType,
    selectedModel,
    selectedCloudProviderId,
    cloudWarningAccepted,
    selectedCloudProvider,
  ]);

  useEffect(() => {
    if (!initialized || !keycloak?.authenticated) return undefined;
    let cancelled = false;
    getAssistants(keycloak)
      .then((rows) => {
        if (!cancelled) setTeamCandidates(Array.isArray(rows) ? rows : []);
      })
      .catch(() => {
        if (!cancelled) setTeamCandidates([]);
      });
    return () => { cancelled = true; };
  }, [initialized, keycloak]);

  useEffect(() => {
    if (!isEditMode || !initialized || !keycloak?.authenticated) {
      setEditLoadDone(true);
      return;
    }
    setEditLoadDone(false);
    let cancelled = false;
    (async () => {
      try {
        const a = await fetchAssistant(keycloak, assistantId);
        if (cancelled) return;
        const exec = a.is_local ? 'on-premise' : 'cloud';
        const displayModel = a.is_local ? a.model : (a.cloud_model || a.model);
        setSelectedExecutionType(exec);
        setLoadedOwnerId(a.owner || null);
        setNumericAssistantId(a.id);
        form.setFieldsValue({
          name: a.name,
          description: a.description,
          system_prompt: a.system_prompt,
          versionTag: a.version || '1',
          stage: a.stage || 'staging',
          visibility: a.group_id ? 'team' : 'private',
          group_id: a.group_id,
          data_collection_id: a.data_collection_id,
          executionType: exec,
          model: displayModel,
          default_collection_attached: a.chat_settings?.default_collection_attached !== false,
          default_tools_enabled: Boolean(a.chat_settings?.default_tools_enabled),
          default_rag_strategy: a.chat_settings?.default_rag_strategy || 'legacy',
          conversation_starters: Array.isArray(a.chat_settings?.conversation_starters)
            ? a.chat_settings.conversation_starters
            : [],
        });
        setSelectedVisibility(a.group_id ? 'team' : 'private');
        setRouterMode(a.router_mode === true);
        setRouterContextualize(a.router_contextualize !== false);
        try {
          const team = await getAssistantTeam(keycloak, assistantId);
          if (!cancelled) {
            setRouterMode(team.router_mode === true);
            setRouterContextualize(team.router_contextualize !== false);
            setTeamMembers(Array.isArray(team.members) ? team.members : []);
          }
        } catch (_) {
          /* team is optional until the first save */
        }
        if (a.data_collection_id) setDataSourceMode('existing');
        else setDataSourceMode('none');
        setSelectedModel(displayModel);
        setAvatarPreviewUrl(a.avatar_url ? mediaUrl(a.avatar_url) : null);
        setAvatarFile(null);
        setCloudWarningAccepted(true);
        loadedCloudRef.current = {
          provider: a.cloud_provider || null,
          model: a.cloud_model || a.model || null,
          providerId: a.cloud_provider_id ?? null,
        };

        if (!a.is_local && a.cloud_provider) {
          const providers = await getCloudCatalogProviders(keycloak).catch(() => []);
          if (cancelled) return;
          setCatalogProviders((prev) => (prev.length ? prev : providers || []));
          const prov = (providers || []).find((p) => p.litellm_provider === a.cloud_provider);
          if (prov) {
            setSelectedCloudProvider(prov);
            setSelectedCloudProviderId(a.cloud_provider_id);
            const models = await getCloudCatalogModels(keycloak, prov.litellm_provider, '', 'chat');
            if (cancelled) return;
            setCatalogModels(models || []);
            const cm = (models || []).find((m) => m.litellm_model_name === a.cloud_model);
            if (cm || a.cloud_model) {
              const modelId = cm?.litellm_model_name ?? a.cloud_model;
              setSelectedModel(modelId);
              form.setFieldsValue({ model: modelId });
            }
          }
        }
        setEditLoadDone(true);
      } catch (e) {
        message.error('Failed to load assistant');
        navigate('/assistants');
      }
    })();
    return () => { cancelled = true; };
  }, [isEditMode, assistantId, initialized, keycloak, form, navigate]);

  const fetchModels = useCallback(async () => {
    if (initialized && keycloak.authenticated) {
      setModelsLoading(true);
      try {
        const fams = await getModelFams(keycloak);
        setModelFamilies(fams);
      } catch (err) {
        console.error('Fetch models error:', err);
      } finally {
        setModelsLoading(false);
      }
    }
  }, [initialized, keycloak]);

  const fetchGroups = useCallback(async () => {
    if (initialized && keycloak.authenticated) {
      try { setGroups((await getGroups(keycloak)) || []); } catch (err) { console.error(err); }
    }
  }, [initialized, keycloak]);

  const fetchDataCollections = useCallback(async () => {
    if (initialized && keycloak.authenticated) {
      try { setDataCollections((await getDataCollections(keycloak)) || []); } catch (err) { console.error(err); }
    }
  }, [initialized, keycloak]);

  const fetchCloudData = useCallback(async () => {
    if (initialized && keycloak.authenticated) {
      try {
        const [providers, userProviders] = await Promise.all([
          getCloudCatalogProviders(keycloak),
          getUserCloudProviders(keycloak)
        ]);
        setCatalogProviders(providers || []);
        setUserCloudProviders(userProviders || []);
      } catch (err) { console.error('Fetch cloud data error:', err); }
    }
  }, [initialized, keycloak]);

  const fetchPlatformSettings = useCallback(async () => {
    if (initialized && keycloak.authenticated) {
      try {
        const settings = await getPlatformSettings(keycloak);
        setCloudModelsEnabled(settings.cloud_models_enabled || false);
        if (settings.assistant_avatar_max_kb != null) {
          setPlatformAvatarMaxKb(Number(settings.assistant_avatar_max_kb));
        }
      } catch (err) {
        // If user is not admin, settings endpoint will fail - default to enabled
        console.error('Failed to load platform settings:', err);
        setCloudModelsEnabled(true);
      }
    }
  }, [initialized, keycloak]);

  // Reset cloud selection if cloud models become disabled
  useEffect(() => {
    if (!cloudModelsEnabled && selectedExecutionType === 'cloud') {
      setSelectedExecutionType(null);
      form.setFieldsValue({ executionType: undefined });
    }
  }, [cloudModelsEnabled, selectedExecutionType, form]);

  useEffect(() => {
    fetchGroups();
    fetchDataCollections();
    fetchCloudData();
    fetchPlatformSettings();
    if (initialized && keycloak?.authenticated) {
      listModules(keycloak).then(setModuleDefinitions).catch(() => setModuleDefinitions([]));
    }
  }, [fetchGroups, fetchDataCollections, fetchCloudData, fetchPlatformSettings, initialized, keycloak]);

  useEffect(() => {
    if (!isEditMode || !initialized || !keycloak?.authenticated || !assistantId) return;
    getAssistantModules(keycloak, assistantId)
      .then((mods) => {
        const map = {};
        (mods || []).forEach((m) => {
          map[m.module_id] = { enabled: m.enabled, settings: m.settings || {} };
        });
        setModuleConfigs(map);
      })
      .catch(() => setModuleConfigs({}));
  }, [isEditMode, assistantId, initialized, keycloak]);

  useEffect(() => {
    if (!initialized || !keycloak?.authenticated) return;
    if (selectedExecutionType !== 'on-premise') return;
    fetchModels();
  }, [initialized, keycloak, selectedExecutionType, fetchModels]);

  useEffect(() => {
    if (!initialized || !keycloak?.authenticated) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await getCurrentUserRoles(keycloak);
        if (cancelled) return;
        if (r?.is_admin) {
          setIsAdmin(true);
          const users = await getUsers(keycloak, null, 0, 200);
          if (cancelled) return;
          setUserOptions(
            (users || []).map((u) => ({
              value: u.id,
              label: u.username || u.email || u.id,
            })),
          );
        } else {
          setIsAdmin(false);
        }
      } catch {
        if (!cancelled) setIsAdmin(false);
      }
    })();
    return () => { cancelled = true; };
  }, [initialized, keycloak]);

  useEffect(() => {
    if (isEditMode || draftPromptDone || !initialized || !keycloak?.authenticated) return;
    let raw;
    try {
      raw = localStorage.getItem(ADD_ASSISTANT_DRAFT_KEY);
    } catch {
      setDraftPromptDone(true);
      return;
    }
    if (!raw) {
      setDraftPromptDone(true);
      return;
    }
    let draft;
    try {
      draft = JSON.parse(raw);
    } catch {
      localStorage.removeItem(ADD_ASSISTANT_DRAFT_KEY);
      setDraftPromptDone(true);
      return;
    }
    if (draft.v !== 1) {
      setDraftPromptDone(true);
      return;
    }
    Modal.confirm({
      title: 'Restore draft?',
      content: 'You have unsaved assistant configuration from a previous session.',
      okText: 'Restore',
      cancelText: 'Discard',
      async onOk() {
        if (draft.values) form.setFieldsValue(draft.values);
        if (draft.dataSourceMode) setDataSourceMode(draft.dataSourceMode);
        if (draft.selectedExecutionType) setSelectedExecutionType(draft.selectedExecutionType);
        if (draft.selectedModel != null) setSelectedModel(draft.selectedModel);
        if (draft.selectedCloudProviderId != null) setSelectedCloudProviderId(draft.selectedCloudProviderId);
        if (typeof draft.cloudWarningAccepted === 'boolean') setCloudWarningAccepted(draft.cloudWarningAccepted);
        if (draft.cloudProviderLitellm && draft.selectedExecutionType === 'cloud') {
          const providers = await getCloudCatalogProviders(keycloak);
          const prov = (providers || []).find((p) => p.litellm_provider === draft.cloudProviderLitellm);
          if (prov) {
            setSelectedCloudProvider(prov);
            const models = await getCloudCatalogModels(keycloak, prov.litellm_provider, '', 'chat');
            setCatalogModels(models || []);
            if (draft.cloudModelName) {
              setSelectedModel(draft.cloudModelName);
              form.setFieldsValue({ model: draft.cloudModelName });
            }
          }
        }
        setDraftPromptDone(true);
      },
      onCancel() {
        try {
          localStorage.removeItem(ADD_ASSISTANT_DRAFT_KEY);
        } catch {
          /* ignore */
        }
        setDraftPromptDone(true);
      },
    });
  }, [isEditMode, draftPromptDone, initialized, keycloak, form]);

  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState === 'hidden') saveDraftToStorage();
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [saveDraftToStorage]);

  useEffect(() => {
    const onBeforeUnload = () => saveDraftToStorage();
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [saveDraftToStorage]);

  useEffect(() => {
    if (isEditMode) return;
    const t = setTimeout(() => saveDraftToStorage(), 0);
    return () => clearTimeout(t);
  }, [dataSourceMode, isEditMode, saveDraftToStorage]);

  // Fetch models when a cloud provider is selected
  useEffect(() => {
    if (selectedCloudProvider && keycloak?.authenticated) {
      setCatalogModelsLoading(true);
      setCatalogModels([]);
      getCloudCatalogModels(keycloak, selectedCloudProvider.litellm_provider, '', 'chat')
        .then(models => setCatalogModels(models || []))
        .catch(err => console.error(err))
        .finally(() => setCatalogModelsLoading(false));
    }
  }, [selectedCloudProvider, keycloak]);

  const startBackgroundPolling = useCallback((assistantId, notifId, assistantName) => {
    let pollAttempts = 0;
    
    const poll = async () => {
      if (!keycloak.authenticated || !assistantId) return;
      pollAttempts++;
      
      try {
        const statusResult = await getAssistantStatus(keycloak, assistantId);
        
        if (statusResult.is_ready) {
          await updateNotification(notifId, {
            title: `${assistantName} is ready`,
            message: 'Assistant initialized successfully and is now running',
            progress: 100,
            status: 'completed',
            dismissible: true,
          });
          return; // Stop polling
        }
        
        if (statusResult.status === 'failed') {
          await updateNotification(notifId, {
            title: `${assistantName} failed to initialize`,
            message: 'Initialization failed. Please try again or check logs.',
            progress: null,
            status: 'error',
            dismissible: true,
          });
          return; // Stop polling
        }
        
        // Still initializing - update progress with meaningful steps
        let progress;
        let msg;
        if (pollAttempts <= 2) {
          progress = 20;
          msg = 'Creating assistant and registering model...';
        } else if (pollAttempts <= 5) {
          progress = 40;
          msg = 'Setting up MLflow model...';
        } else if (pollAttempts <= 10) {
          progress = 60;
          msg = 'Downloading and configuring model...';
        } else if (pollAttempts <= 20) {
          progress = 80;
          msg = 'Finalizing initialization...';
        } else {
          progress = 90;
          msg = 'Almost ready, please wait...';
        }
        
        await updateNotification(notifId, {
          progress,
          message: msg,
        });
        
        // Continue polling (setTimeout is not tied to component lifecycle)
        setTimeout(poll, 3000);
      } catch (error) {
        // On transient errors, retry a few times before giving up
        if (pollAttempts < 30) {
          setTimeout(poll, 5000);
        } else {
          await updateNotification(notifId, {
            title: `${assistantName} initialization status unknown`,
            message: error.message || 'Lost connection while checking status',
            status: 'error',
            dismissible: true,
          });
        }
      }
    };
    
    // Start first poll after a short delay
    setTimeout(poll, 2000);
  }, [keycloak, updateNotification]);

  const allModels = useMemo(
    () =>
      modelFamilies.flatMap((family) =>
        family.models.map((model) => ({
          ...model,
          familyName: family.name,
          familyDescription: family.description,
          familyIcon: family.icon,
          familyUrl: family.url,
          fullName: model.name,
        })),
      ),
    [modelFamilies],
  );


  const handleCloudModelChange = (modelId) => {
    setSelectedModel(modelId);
    form.setFieldsValue({ model: modelId });
  };

  const onSelectExecutionType = (key) => {
    setSelectedExecutionType(key);
    form.setFieldsValue({ executionType: key });
    setSelectedModel(null);
    setSelectedCloudProvider(null);
    setSelectedCloudProviderId(null);
    setCloudWarningAccepted(false);
    setModelSearch('');
    form.setFieldsValue({ model: undefined });
  };

  const handleSelectCloudProvider = (provider) => {
    setSelectedCloudProvider(provider);
    setSelectedModel(null);
    setModelSearch('');
    form.setFieldsValue({ model: undefined });
    // Auto-select matching API key
    const config = userCloudProviders.find(p => p.provider === provider.litellm_provider && p.is_active);
    setSelectedCloudProviderId(config ? config.id : null);
  };

  const onAvatarBeforeUpload = (file) => {
    const maxKb = platformAvatarMaxKb || 512;
    if (file.size > maxKb * 1024) {
      message.error(`Image must be ${maxKb} KB or smaller`);
      return Upload.LIST_IGNORE;
    }
    if (isEditMode) {
      setAvatarUploading(true);
      uploadAssistantAvatar(keycloak, assistantId, file)
        .then((res) => {
          setAvatarPreviewUrl(mediaUrl(res.avatar_url));
          message.success('Avatar updated');
        })
        .catch((e) => message.error(e.message || 'Upload failed'))
        .finally(() => setAvatarUploading(false));
      return false;
    }
    setAvatarFile(file);
    setAvatarPreviewUrl((prev) => {
      if (prev && prev.startsWith('blob:')) URL.revokeObjectURL(prev);
      return URL.createObjectURL(file);
    });
    return false;
  };

  const handleAddProvider = async (values) => {
    setAddingProvider(true);
    try {
      const result = await createCloudProvider(keycloak, {
        name: values.name, provider: values.provider,
        api_key: values.api_key, api_base: values.api_base || null,
      });
      message.success('API key saved!');
      setShowAddProviderModal(false);
      addProviderForm.resetFields();
      const updatedProviders = await getUserCloudProviders(keycloak);
      setUserCloudProviders(updatedProviders || []);
      if (selectedCloudProvider?.litellm_provider === values.provider) {
        setSelectedCloudProviderId(result.id);
      }
    } catch (err) { message.error(`Failed: ${err.message}`); }
    finally { setAddingProvider(false); }
  };

  const handleCollectionModalCreate = async (formData, collectionName) => {
    const res = await createDataCollection(keycloak, formData);
    message.success(`Collection "${collectionName}" created`);
    setCreateCollectionOpen(false);
    await fetchDataCollections();
    if (res?.id != null) {
      form.setFieldsValue({ data_collection_id: res.id });
      setDataSourceMode('existing');
    }
  };

  const providerConfigs = selectedCloudProvider
    ? userCloudProviders.filter(p => p.provider === selectedCloudProvider.litellm_provider && p.is_active)
    : [];
  const hasApiKeyForProvider = providerConfigs.length > 0;

  const onFinish = async (values) => {
    if (!keycloak.authenticated) {
      message.error('Not logged in.');
      return;
    }
    const isCloud = selectedExecutionType === 'cloud';

    const resolveDataCollectionId = async () => {
      if (dataSourceMode === 'none') return null;
      if (dataSourceMode === 'existing') return values.data_collection_id ?? null;
      const f = fileList[0]?.originFileObj;
      if (!f) {
        throw new Error('Please choose a data file to upload');
      }
      const uploadRes = await uploadCollectionFileLegacy(keycloak, f, {
        group_id: values.visibility === 'team' ? values.group_id : undefined,
      });
      return uploadRes?.id ?? null;
    };

    if (isEditMode) {
      if (dataSourceMode === 'upload' && !fileList[0]?.originFileObj) {
        message.error('Please choose a file to upload for the data collection.');
        return;
      }
      if (dataSourceMode === 'existing' && values.data_collection_id == null) {
        message.error('Please select a data collection.');
        return;
      }
      if (isCloud) {
        const cloudProvider = selectedCloudProvider?.litellm_provider ?? loadedCloudRef.current.provider;
        const cloudModel = (values.model || selectedModel || '').trim() || loadedCloudRef.current.model;
        const cloudProviderId = selectedCloudProviderId ?? loadedCloudRef.current.providerId;
        if (!cloudProvider || !cloudModel) {
          message.error('Please select a cloud provider and model.');
          return;
        }
        if (!cloudProviderId) {
          message.error('Please add an API key for the selected provider.');
          return;
        }
      }
      setIsLoading(true);
      try {
        let dataCollectionId = null;
        if (dataSourceMode === 'upload') {
          dataCollectionId = await resolveDataCollectionId();
        } else if (dataSourceMode === 'existing') {
          dataCollectionId = values.data_collection_id ?? null;
        }

        const patch = {
          name: values.name,
          description: values.description,
          system_prompt: values.system_prompt,
          version: values.versionTag || null,
          stage: values.stage || null,
          group_id: values.visibility === 'team' ? values.group_id : null,
          data_collection_id: dataCollectionId,
          chat_settings: buildChatSettings(values, { routerMode, contextualize: routerContextualize }),
        };

        if (isCloud) {
          const cloudProvider = selectedCloudProvider?.litellm_provider ?? loadedCloudRef.current.provider;
          const cloudModel = (values.model || selectedModel || '').trim() || loadedCloudRef.current.model;
          const cloudProviderId = selectedCloudProviderId ?? loadedCloudRef.current.providerId;
          Object.assign(patch, {
            is_local: false,
            model: cloudModel,
            cloud_provider: cloudProvider,
            cloud_model: cloudModel,
            cloud_provider_id: cloudProviderId,
          });
        } else {
          const mname = (values.model || '').trim();
          if (!mname) throw new Error('Please select a model');
          const obj = allModels.find((m) => m.fullName === mname);
          Object.assign(patch, {
            is_local: true,
            model: obj ? obj.fullName : mname,
          });
        }

        if (routerMode && teamMembers.some((member) => !(member.routing_hint || '').trim())) {
          message.error('Each specialist needs a short note on when to use it.');
          setIsLoading(false);
          return;
        }
        await updateAssistant(keycloak, assistantId, patch);
        await saveAssistantTeam(keycloak, assistantId, {
          router_mode: routerMode,
          router_contextualize: routerContextualize,
          members: teamMembers.map((member, index) => ({
            assistant_id: member.assistant_id,
            routing_hint: (member.routing_hint || '').trim(),
            position: index,
            enabled: member.enabled !== false,
          })),
        });
        const modulesPayload = (moduleDefinitions.length ? moduleDefinitions : [{ module_id: 'program_selection' }]).map((def) => {
          const id = def.module_id;
          const cfg = moduleConfigs[id] || {};
          return {
            module_id: id,
            enabled: Boolean(cfg.enabled),
            settings: cfg.settings || {},
          };
        });
        await updateAssistantModules(keycloak, assistantId, modulesPayload);
        try {
          localStorage.removeItem(ADD_ASSISTANT_DRAFT_KEY);
        } catch (_) {
          /* ignore */
        }
        message.success('Assistant updated');
        navigate('/assistants');
      } catch (err) {
        message.error(err.message || 'Update failed');
      } finally {
        setIsLoading(false);
      }
      return;
    }

    if (isCloud) {
      if (!selectedCloudProvider) {
        message.error('Please select a cloud provider.');
        return;
      }
      if (!(values.model || selectedModel || '').trim()) {
        message.error('Please select or enter a model.');
        return;
      }
      if (!selectedCloudProviderId) {
        message.error('Please add an API key for the selected provider.');
        return;
      }
      if (!cloudWarningAccepted) {
        message.error('Please acknowledge the third-party data warning.');
        return;
      }
    }

    if (dataSourceMode === 'upload' && !fileList[0]?.originFileObj) {
      message.error('Please choose a file to upload for the data collection.');
      return;
    }
    if (routerMode && teamMembers.some((member) => !(member.routing_hint || '').trim())) {
      message.error('Each specialist needs a short note on when to use it.');
      return;
    }

    setIsLoading(true);
    try {
      let payload;
      if (isCloud) {
        const cloudModel = (values.model || selectedModel || '').trim();
        payload = {
          name: values.name,
          description: values.description,
          system_prompt: values.system_prompt,
          version: values.versionTag || null,
          stage: values.stage || null,
          model: cloudModel,
          is_local: false,
          cloud_provider: selectedCloudProvider.litellm_provider,
          cloud_model: cloudModel,
          cloud_provider_id: selectedCloudProviderId,
          group_id: values.visibility === 'team' ? values.group_id : null,
          data_collection_id: dataSourceMode === 'existing' ? values.data_collection_id : null,
        };
      } else {
        const name = (values.model || '').trim();
        if (!name) throw new Error('Please select or enter a model name');
        const obj = allModels.find((m) => m.fullName === name);
        payload = {
          name: values.name,
          description: values.description,
          system_prompt: values.system_prompt,
          version: values.versionTag || null,
          stage: values.stage || null,
          model: obj ? obj.fullName : name,
          is_local: true,
          group_id: values.visibility === 'team' ? values.group_id : null,
          data_collection_id: dataSourceMode === 'existing' ? values.data_collection_id : null,
        };
      }

      const response = await createAssistant(keycloak, payload);

      if (response?.id) {
        await updateAssistant(keycloak, response.id, {
          chat_settings: buildChatSettings(values, { routerMode, contextualize: routerContextualize }),
        });
        await saveAssistantTeam(keycloak, response.id, {
          router_mode: routerMode,
          router_contextualize: routerContextualize,
          members: teamMembers.map((member, index) => ({
            assistant_id: member.assistant_id,
            routing_hint: (member.routing_hint || '').trim(),
            position: index,
            enabled: member.enabled !== false,
          })),
        });
      }

      if (dataSourceMode === 'upload' && response?.id) {
        const f = fileList[0]?.originFileObj;
        const uploadRes = await uploadCollectionFileLegacy(keycloak, f, {
          group_id: values.visibility === 'team' ? values.group_id : undefined,
        });
        await updateAssistant(keycloak, response.id, { data_collection_id: uploadRes.id });
        message.info('Data file uploaded; indexing runs in the background.');
      }

      if (avatarFile && response?.id) {
        try {
          await uploadAssistantAvatar(keycloak, response.id, avatarFile);
        } catch (e) {
          message.warning(`Assistant created but avatar upload failed: ${e.message || e}`);
        }
      }
      if (response?.id) {
        const modulesPayload = (moduleDefinitions.length ? moduleDefinitions : [{ module_id: 'program_selection' }]).map((def) => {
          const id = def.module_id;
          const cfg = moduleConfigs[id] || {};
          return {
            module_id: id,
            enabled: Boolean(cfg.enabled),
            settings: cfg.settings || {},
          };
        });
        try {
          await updateAssistantModules(keycloak, response.id, modulesPayload);
        } catch (e) {
          message.warning(`Assistant created but module settings failed: ${e.message || e}`);
        }
      }
      try {
        localStorage.removeItem(ADD_ASSISTANT_DRAFT_KEY);
      } catch (_) {
        /* ignore */
      }
      if (isCloud) {
        await addNotification({
          type: 'success',
          title: 'Assistant created',
          message: `${values.name} is ready to use`,
          status: 'completed',
          dismissible: true,
        });
        message.success('Cloud assistant created!');
        navigate('/assistants');
      } else {
        const notifId = await addNotification({
          type: 'info',
          title: `Initializing ${values.name}`,
          message: 'Creating assistant and registering model...',
          progress: 10,
          status: 'processing',
          dismissible: false,
        });

        message.info(`${values.name} is being initialized. You can track progress in notifications.`);

        startBackgroundPolling(response.id, notifId, values.name);

        navigate('/assistants');
      }
    } catch (err) {
      message.error(`Failed: ${err.message || 'Unknown error'}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDuplicateAssistant = async () => {
    if (!assistantId || duplicating) return;
    setDuplicating(true);
    try {
      const created = await duplicateAssistant(keycloak, assistantId);
      message.success(`Duplicated as "${created.name}"`);
      navigate(`/assistants/${created.public_id || created.id}/edit`);
    } catch (err) {
      message.error(`Failed to duplicate assistant: ${err.message || 'Unknown error'}`);
    } finally {
      setDuplicating(false);
    }
  };

  return (
    <div className="page-container add-assistant-page">
      <div className="add-assistant-form-panel collections-table-wrapper">
        <Spin spinning={isEditMode && !editLoadDone}>
        <Form
          form={form}
          className="add-assistant-form-inner"
          layout="vertical"
          onFinish={onFinish}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && event.target.closest('.ant-select')) {
              event.preventDefault();
            }
          }}
          initialValues={{
            versionTag: '1',
            stage: 'staging',
            visibility: 'private',
          }}
          onValuesChange={() => {
            if (isEditMode) return;
            if (draftSaveTimerRef.current) clearTimeout(draftSaveTimerRef.current);
            draftSaveTimerRef.current = setTimeout(() => saveDraftToStorage(), 600);
          }}
          scrollToFirstError
          onFinishFailed={({ errorFields }) => {
            const first = errorFields[0];
            if (first?.name) setSettingsChapter(chapterForField(first.name));
            const messages = errorFields.flatMap((f) => f.errors).filter(Boolean);
            if (messages.length > 0) {
              message.error(messages[0]);
            } else {
              message.error('Please fix the highlighted fields before saving.');
            }
          }}
        >
          <div className="settings-shell">
          <nav className="settings-side" role="tablist" aria-label="Settings">
            {SETTINGS_CHAPTERS.map((chapter) => (
              <button
                key={chapter.id}
                type="button"
                role="tab"
                aria-selected={settingsChapter === chapter.id}
                className={`settings-side-item${settingsChapter === chapter.id ? ' is-selected' : ''}`}
                onClick={() => {
                  setSettingsChapter(chapter.id);
                  window.scrollTo(0, 0);
                }}
              >
                {chapter.label}
              </button>
            ))}
          </nav>
          <div className="settings-main">
          <div className="add-assistant-page-intro">
            <Breadcrumb
              className="add-assistant-breadcrumb"
              items={[
                { title: <Link to="/assistants">Assistants</Link> },
                { title: isEditMode ? 'Edit Assistant' : 'Add Assistant' },
              ]}
            />
            <div className="collections-page-header add-assistant-page-header">
              <div className="page-header">
                <h1 className="add-assistant-page-title">
                  {isEditMode ? 'Edit Assistant' : 'Add Assistant'}
                </h1>
                <p className="add-assistant-page-subtitle">
                  {isEditMode
                    ? 'Update instructions, access, and data sources.'
                    : 'Configure and deploy a new assistant.'}
                </p>
              </div>
              {isEditMode && (
                <Dropdown
                  trigger={['click']}
                  placement="bottomRight"
                  menu={{
                    items: [
                      {
                        key: 'duplicate',
                        icon: <CopyOutlined />,
                        label: 'Duplicate',
                        disabled: duplicating,
                        onClick: handleDuplicateAssistant,
                      },
                      {
                        key: 'channels',
                        icon: <ApiOutlined />,
                        label: 'Channels',
                        onClick: () => navigate(`/assistants/${assistantId}/channels`),
                      },
                    ],
                  }}
                >
                  <Button
                    type="text"
                    className="add-assistant-more"
                    icon={<MoreOutlined />}
                    aria-label="More actions"
                  />
                </Dropdown>
              )}
            </div>
          </div>
          <div className={`settings-chapter${settingsChapter === 'general' ? ' is-active' : ''}`}>
          <SettingsPageHead
            title="General"
            description="Name, description, and the instructions this assistant follows."
          />
          <SettingsPanel>
            <SettingsField label="Name" help="Shown in the assistant list and in chat.">
              <Form.Item
                name="name"
                rules={[{ required: true, message: 'Please enter a name' }]}
                style={{ marginBottom: 0, width: '100%' }}
              >
                <Input placeholder="Support bot" />
              </Form.Item>
            </SettingsField>
            <SettingsField label="Description" help="One or two lines on what this assistant is for.">
              <Form.Item name="description" noStyle>
                <Input.TextArea rows={3} placeholder="Helps customers with common product questions" />
              </Form.Item>
            </SettingsField>
            <SettingsField
              label="Avatar"
              help={`PNG, JPG, GIF, or WebP. Max ${platformAvatarMaxKb} KB.`}
            >
              <Upload
                name="avatar"
                showUploadList={false}
                beforeUpload={onAvatarBeforeUpload}
                accept="image/png,image/jpeg,image/gif,image/webp"
                disabled={avatarUploading}
              >
                <button type="button" className="settings-avatar" aria-label="Replace avatar">
                  {avatarPreviewUrl ? (
                    <img src={avatarPreviewUrl} alt="" />
                  ) : (
                    <PlusOutlined />
                  )}
                </button>
              </Upload>
            </SettingsField>
            <SettingsField
              label="Instructions"
              help="How this assistant should behave in conversation."
              stack
            >
              <Form.Item name="system_prompt" className="settings-instructions" style={{ marginBottom: 0 }}>
                <PromptEditor
              binding={
                isEditMode && assistantId
                  ? {
                      targetType: 'assistant',
                      targetId: numericAssistantId,
                      slot: 'system_prompt',
                      name: watchedAssistantName ? `${watchedAssistantName} — Instructions` : undefined,
                    }
                  : null
              }
              models={
                form.getFieldValue('is_local') === false
                  ? [form.getFieldValue('cloud_model') || form.getFieldValue('model')].filter(Boolean)
                  : [form.getFieldValue('model')].filter(Boolean)
              }
              features={{ library: isEditMode }}
                />
              </Form.Item>
            </SettingsField>
          </SettingsPanel>
          </div>

          <div className={`settings-chapter${settingsChapter === 'model' ? ' is-active' : ''}`}>
          <SettingsPageHead title="Model" description="Choose where this assistant runs." />
            <div className="settings-choice-grid">
              {executionTypeOptions.map(({ key, title, description, icon, iconClass }) => {
                const isCloud = key === 'cloud';
                const isDisabled = isCloud && !cloudModelsEnabled;
                const selected = selectedExecutionType === key;
                return (
                  <button
                    type="button"
                    key={key}
                    disabled={isDisabled}
                    className={`settings-choice ${selected ? 'is-selected' : ''}`}
                    onClick={() => !isDisabled && onSelectExecutionType(key)}
                    aria-pressed={selected}
                  >
                    <div className={`settings-choice-icon ${iconClass}`}>
                      {icon}
                    </div>
                    <div className="settings-choice-body">
                      <span className="settings-choice-title">{title}</span>
                      <span className="settings-choice-desc">
                        {isDisabled ? 'Cloud models are disabled' : description}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          <Form.Item name="executionType" rules={!isEditMode ? [{ required: true, message: 'Please select where to host' }] : []} hidden>
            <Input type="hidden" />
          </Form.Item>

          {selectedExecutionType === 'cloud' && (
            <>
              <SettingsPanel>
                <SettingsField
                  label="Third-party data"
                  help="Messages and files are sent to a third-party provider and may be retained under that provider's terms."
                >
                  <Switch
                    checked={cloudWarningAccepted}
                    onChange={setCloudWarningAccepted}
                    aria-label="I understand and accept the risks"
                  />
                </SettingsField>
                <SettingsField
                  label="Provider"
                  help={(
                    <>
                      Host for this assistant.
                      {' '}
                      <a
                        className="settings-quiet-link"
                        href="https://docs.litellm.ai/docs/providers"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Provider docs
                      </a>
                    </>
                  )}
                >
                  <Select
                    showSearch
                    placeholder="Search providers"
                    optionFilterProp="label"
                    value={selectedCloudProvider?.litellm_provider}
                    onChange={(litellm) => {
                      const provider = catalogProviders.find((item) => item.litellm_provider === litellm);
                      if (provider) handleSelectCloudProvider(provider);
                    }}
                    notFoundContent="No providers found. Run catalog sync from admin panel."
                  >
                    {catalogProviders.map((provider) => (
                      <Option key={provider.id} value={provider.litellm_provider} label={provider.name}>
                        {provider.name}
                      </Option>
                    ))}
                  </Select>
                </SettingsField>
                {selectedCloudProvider && !hasApiKeyForProvider && (
                  <SettingsField
                    label="API key"
                    help={`${selectedCloudProvider.name} needs a key before you can choose a model.`}
                  >
                    <Button
                      icon={<PlusOutlined />}
                      onClick={() => {
                        addProviderForm.setFieldsValue({ provider: selectedCloudProvider.litellm_provider });
                        setShowAddProviderModal(true);
                      }}
                    >
                      Add API key
                    </Button>
                  </SettingsField>
                )}
                {selectedCloudProvider && hasApiKeyForProvider && (
                  <SettingsField label="API key" help="Stored key used to call this provider.">
                    <div className="settings-field-stack">
                      <Select
                        value={selectedCloudProviderId}
                        onChange={(value) => setSelectedCloudProviderId(value)}
                        placeholder="Select API key"
                      >
                        {providerConfigs.map((config) => (
                          <Option key={config.id} value={config.id}>
                            <span><KeyOutlined style={{ marginRight: 8 }} />{config.name}</span>
                            <span style={{ float: 'right', color: 'var(--color-text-secondary)', fontSize: 12 }}>{config.api_key_preview}</span>
                          </Option>
                        ))}
                      </Select>
                      <button
                        type="button"
                        className="settings-quiet-link"
                        onClick={() => {
                          addProviderForm.setFieldsValue({ provider: selectedCloudProvider.litellm_provider });
                          setShowAddProviderModal(true);
                        }}
                      >
                        Add another key
                      </button>
                    </div>
                  </SettingsField>
                )}
              </SettingsPanel>
              {selectedCloudProvider && hasApiKeyForProvider && (
                <SettingsBlock>
                  <CatalogChatModelField
                    models={catalogModels}
                    loading={catalogModelsLoading}
                    search={modelSearch}
                    onSearchChange={setModelSearch}
                    value={selectedModel || ''}
                    onChange={handleCloudModelChange}
                    emptyDescription="No chat models for this provider. Sync the catalog in Platform Settings, or type a LiteLLM model id below."
                  />
                </SettingsBlock>
              )}
            </>
          )}

          {selectedExecutionType === 'on-premise' && (
            <SettingsBlock>
              <LocalOllamaModelField
                value={selectedModel || ''}
                onChange={(v) => {
                  setSelectedModel(v);
                  form.setFieldsValue({ model: v });
                }}
                families={modelFamilies}
                loading={modelsLoading}
                emptyDescription="No models in catalog match. Type a full Ollama model name above or check that Ollama is running."
                onPersistCustom={async (name) => {
                  await registerOllamaChatModel(keycloak, name);
                  const fams = await getModelFams(keycloak);
                  setModelFamilies(Array.isArray(fams) ? fams : []);
                }}
              />
            </SettingsBlock>
          )}

          <Form.Item name="model" hidden rules={isEditMode ? [] : [{ required: true, message: 'Please select a model' }]}>
            <Input type="hidden" />
          </Form.Item>
          </div>

          <div className={`settings-chapter${settingsChapter === 'knowledge' ? ' is-active' : ''}`}>
          <SettingsPageHead title="Knowledge" description="Where this assistant looks for answers." />
          <SettingsBlock title="Data source">
            <div className="settings-choice-grid is-three">
              <button
                type="button"
                className={`settings-choice ${dataSourceMode === 'none' ? 'is-selected' : ''}`}
                onClick={() => {
                  setDataSourceMode('none');
                  form.setFieldsValue({ data_collection_id: undefined });
                }}
                aria-pressed={dataSourceMode === 'none'}
              >
                <div className="settings-choice-icon add-assistant-host-icon-local">
                  <DatabaseOutlined />
                </div>
                <div className="settings-choice-body">
                  <span className="settings-choice-title">None</span>
                  <span className="settings-choice-desc">No collection</span>
                </div>
              </button>
              <button
                type="button"
                className={`settings-choice ${dataSourceMode === 'existing' ? 'is-selected' : ''}`}
                onClick={() => setDataSourceMode('existing')}
                aria-pressed={dataSourceMode === 'existing'}
              >
                <div className="settings-choice-icon add-assistant-host-icon-cloud">
                  <DatabaseOutlined />
                </div>
                <div className="settings-choice-body">
                  <span className="settings-choice-title">Collection</span>
                  <span className="settings-choice-desc">Use an existing one</span>
                </div>
              </button>
              <button
                type="button"
                className={`settings-choice ${dataSourceMode === 'upload' ? 'is-selected' : ''}`}
                onClick={() => {
                  setDataSourceMode('upload');
                  form.setFieldsValue({ data_collection_id: undefined });
                }}
                aria-pressed={dataSourceMode === 'upload'}
              >
                <div className="settings-choice-icon add-assistant-host-icon-cloud">
                  <InboxOutlined />
                </div>
                <div className="settings-choice-body">
                  <span className="settings-choice-title">Upload</span>
                  <span className="settings-choice-desc">CSV or spreadsheet</span>
                </div>
              </button>
            </div>
          {dataSourceMode === 'existing' && (
            <SettingsPanel>
              <SettingsField label="Collection" help="Documents this assistant can search.">
                <Form.Item name="data_collection_id" style={{ marginBottom: 0, width: '100%' }}>
                  <Select
                    placeholder="Search collections…"
                    showSearch
                    allowClear
                    optionFilterProp="children"
                    popupRender={(menu) => (
                      <>
                        <div style={{ padding: '4px 8px 8px', borderBottom: '1px solid #f0f0f0' }}>
                          <Button
                            type="link"
                            block
                            icon={<PlusOutlined />}
                            style={{ justifyContent: 'flex-start', height: 'auto' }}
                            onClick={() => setCreateCollectionOpen(true)}
                          >
                            Create new data collection…
                          </Button>
                        </div>
                        {menu}
                      </>
                    )}
                  >
                    {dataCollections.map((c) => (
                      <Option key={c.id} value={c.id}>
                        <span>{c.name}</span>
                        <span style={{ float: 'right', color: '#8c8c8c', fontSize: 12 }}>{c.row_count?.toLocaleString() || 0} rows</span>
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </SettingsField>
            </SettingsPanel>
          )}
          {dataSourceMode === 'upload' && (
            <SettingsPanel>
              <SettingsField label="File" help="CSV or spreadsheet. It becomes a new collection.">
                <Form.Item name="dataFile" style={{ marginBottom: 0 }}>
                  <Upload
                    fileList={fileList}
                    maxCount={1}
                    beforeUpload={() => false}
                    onChange={({ fileList: next }) => setFileList(next)}
                    accept=".csv,.xlsx,.xls"
                  >
                    <Button icon={<InboxOutlined />}>Choose a file</Button>
                  </Upload>
                </Form.Item>
              </SettingsField>
            </SettingsPanel>
          )}
          </SettingsBlock>
          <SettingsBlock title="New chats">
          <SettingsPanel>
            <SettingsField label="Collection" help="New chats start with this collection attached.">
              <Form.Item name="default_collection_attached" valuePropName="checked" initialValue noStyle>
                <Switch />
              </Form.Item>
            </SettingsField>
            <SettingsField label="Tools" help="New chats start with tools turned on.">
              <Form.Item name="default_tools_enabled" valuePropName="checked" initialValue={false} noStyle>
                <Switch />
              </Form.Item>
            </SettingsField>
            <SettingsField
              label="How answers use the collection"
              help="Search first looks up the collection, then writes the answer. Assistant searches looks things up while it is writing."
            >
              <Form.Item name="default_rag_strategy" initialValue="legacy" noStyle>
                <SettingsSegment
                  ariaLabel="How answers use the collection"
                  options={[
                    { value: 'legacy', text: 'Search first' },
                    { value: 'agent_led', text: 'Assistant searches' },
                  ]}
                />
              </Form.Item>
            </SettingsField>
          </SettingsPanel>
          </SettingsBlock>
          <SettingsBlock title="Conversation starters">
          <SettingsPanel>
            <SettingsField
              stack
              label="Empty chat"
              help="Up to eight lines. They show before the first message. A tap puts one in the box."
            >
              <Form.List name="conversation_starters">
                {(fields, { add, remove }) => (
                  <div className="starter-editor">
                    {fields.map((field) => (
                      <div key={field.key} className="starter-editor-row">
                        <Form.Item name={field.name} noStyle>
                          <Input maxLength={200} placeholder="A question people can start with" />
                        </Form.Item>
                        <Button
                          type="text"
                          icon={<DeleteOutlined />}
                          aria-label="Remove starter"
                          onClick={() => remove(field.name)}
                        />
                      </div>
                    ))}
                    {fields.length < 8 ? (
                      <Button type="dashed" icon={<PlusOutlined />} onClick={() => add('')}>
                        Add a starter
                      </Button>
                    ) : null}
                  </div>
                )}
              </Form.List>
            </SettingsField>
          </SettingsPanel>
          </SettingsBlock>
          </div>

          <div className={`settings-chapter${settingsChapter === 'capabilities' ? ' is-active' : ''}`}>
          <SettingsPageHead
            title="Capabilities"
            description="Routing, modules, and the tools this assistant can use."
          />
          <AssistantTeamSection
            routerMode={routerMode}
            onRouterMode={setRouterMode}
            contextualize={routerContextualize}
            onContextualize={setRouterContextualize}
            members={teamMembers}
            onMembersChange={setTeamMembers}
            candidates={teamCandidates}
            selfId={numericAssistantId}
          />

          <SettingsBlock title="Modules">
          <SettingsPanel>
          {(moduleDefinitions.length ? moduleDefinitions : [{
            module_id: 'program_selection',
            display_name: 'Подбор образовательной программы',
            description: 'Пошаговый подбор ОП для абитуриента',
            default_settings: {},
          }]).map((mod) => {
            const cfg = moduleConfigs[mod.module_id] || { enabled: false, settings: mod.default_settings || {} };
            return (
              <React.Fragment key={mod.module_id}>
                <SettingsField
                  label={mod.display_name}
                  help={mod.description || 'Structured flow this assistant can run in chat.'}
                >
                  <Switch
                    checked={Boolean(cfg.enabled)}
                    onChange={(checked) => {
                      setModuleConfigs((prev) => ({
                        ...prev,
                        [mod.module_id]: { ...cfg, enabled: checked },
                      }));
                    }}
                  />
                </SettingsField>
                {cfg.enabled && (
                  <>
                    <SettingsField label="URL оператора / приёма">
                      <Input
                        value={cfg.settings?.operator_handoff_url || ''}
                        placeholder="https://priem.mirea.ru/"
                        onChange={(e) => {
                          setModuleConfigs((prev) => ({
                            ...prev,
                            [mod.module_id]: {
                              ...cfg,
                              settings: { ...cfg.settings, operator_handoff_url: e.target.value },
                            },
                          }));
                        }}
                      />
                    </SettingsField>
                    <SettingsField label="Календарь мероприятий">
                      <Input
                        value={cfg.settings?.events_calendar_url || ''}
                        placeholder="https://priem.mirea.ru/"
                        onChange={(e) => {
                          setModuleConfigs((prev) => ({
                            ...prev,
                            [mod.module_id]: {
                              ...cfg,
                              settings: { ...cfg.settings, events_calendar_url: e.target.value },
                            },
                          }));
                        }}
                      />
                    </SettingsField>
                    {mod.module_id === 'program_selection' && (() => {
                      const egeSubjects = (
                        cfg.settings?.ege_subjects?.length
                          ? cfg.settings.ege_subjects
                          : mod.default_settings?.ege_subjects
                      ) || [];
                      const patchSubjects = (next) => {
                        setModuleConfigs((prev) => ({
                          ...prev,
                          [mod.module_id]: {
                            ...cfg,
                            settings: { ...cfg.settings, ege_subjects: next },
                          },
                        }));
                      };
                      return (
                        <SettingsField
                          label="Предметы ЕГЭ"
                          help="Список предметов для чипов в чате. Ключ — латиница без пробелов."
                          stack
                        >
                          {egeSubjects.map((subj, idx) => (
                            <div key={subj.key || `ege-${idx}`} className="settings-subject-row">
                              <Input
                                className="settings-subject-key"
                                value={subj.key || ''}
                                placeholder="key"
                                onChange={(e) => {
                                  const next = egeSubjects.map((s, i) => (
                                    i === idx ? { ...s, key: e.target.value } : s
                                  ));
                                  patchSubjects(next);
                                }}
                              />
                              <Input
                                className="settings-subject-label"
                                value={subj.label || ''}
                                placeholder="Название предмета"
                                onChange={(e) => {
                                  const next = egeSubjects.map((s, i) => (
                                    i === idx ? { ...s, label: e.target.value } : s
                                  ));
                                  patchSubjects(next);
                                }}
                              />
                              <Input
                                className="settings-subject-score"
                                type="number"
                                min={0}
                                max={100}
                                value={subj.min_score ?? 40}
                                placeholder="мин"
                                onChange={(e) => {
                                  const val = parseInt(e.target.value, 10);
                                  const next = egeSubjects.map((s, i) => (
                                    i === idx ? { ...s, min_score: Number.isNaN(val) ? 40 : val } : s
                                  ));
                                  patchSubjects(next);
                                }}
                              />
                              <Button
                                className="settings-icon-btn"
                                type="text"
                                danger
                                aria-label="Remove subject"
                                disabled={egeSubjects.length <= 1}
                                onClick={() => patchSubjects(egeSubjects.filter((_, i) => i !== idx))}
                              >
                                ×
                              </Button>
                            </div>
                          ))}
                          <Button
                            icon={<PlusOutlined />}
                            onClick={() => patchSubjects([
                              ...egeSubjects,
                              { key: `subject_${egeSubjects.length + 1}`, label: 'Новый предмет', min_score: 40 },
                            ])}
                          >
                            Добавить предмет
                          </Button>
                        </SettingsField>
                      );
                    })()}
                  </>
                )}
              </React.Fragment>
            );
          })}
          </SettingsPanel>
          </SettingsBlock>

          <SettingsBlock title="Tools">
          <SettingsPanel padded>
          <AssistantToolsSection
            keycloak={keycloak}
            assistantId={assistantId}
            isEditMode={isEditMode}
          />
          </SettingsPanel>
          </SettingsBlock>
          </div>

          <div className={`settings-chapter${settingsChapter === 'access' ? ' is-active' : ''}`}>
          <SettingsPageHead title="Access" description="Who can use this assistant." />
          <SettingsPanel>
            <SettingsField label="Who can use it" help="Private stays with you. Team shares it with a group.">
            <SettingsSegment
              ariaLabel="Access"
              value={selectedVisibility}
              onChange={(value) => {
                setSelectedVisibility(value);
                form.setFieldsValue({ visibility: value });
              }}
              options={[
                { value: 'private', text: 'Private' },
                { value: 'team', text: 'Team' },
              ]}
            />
            </SettingsField>
            <Form.Item name="visibility" hidden>
              <Input type="hidden" />
            </Form.Item>
            {selectedVisibility === 'team' && (
              <SettingsField label="Group" help="People in this group can use the assistant.">
              <Form.Item
                name="group_id"
                rules={[{ required: true, message: 'Please select a group' }]}
                style={{ marginBottom: 0, width: '100%' }}
              >
                <Select placeholder="Choose a group" showSearch>
                  {groups.map((group) => <Option key={group.id} value={group.id}>{group.name}</Option>)}
                </Select>
              </Form.Item>
              </SettingsField>
            )}
            {isEditMode && isAdmin && loadedOwnerId && (
              <SettingsField label="Owner" help="The person who can change this assistant.">
                <AssistantOwnerSelect
                  ownerId={loadedOwnerId}
                  options={userOptions}
                  assistantId={assistantId}
                  assistantName={watchedAssistantName}
                  keycloak={keycloak}
                  onUpdated={async () => {
                    const a = await fetchAssistant(keycloak, assistantId);
                    setLoadedOwnerId(a.owner);
                  }}
                />
              </SettingsField>
            )}
          </SettingsPanel>

          <SettingsBlock title="Details">
          <SettingsPanel>
            <SettingsField label="Tags" help="Short labels for finding this assistant later.">
            <Form.Item name="tags" noStyle>
              <Select mode="tags" placeholder="support, billing" />
            </Form.Item>
            </SettingsField>
            <SettingsField label="Version" help="A label for this revision, such as 1.0.0.">
            <Form.Item name="versionTag" noStyle>
              <Input placeholder="1.0.0" />
            </Form.Item>
            </SettingsField>
            <SettingsField label="Stage" help="Where this assistant sits in its lifecycle.">
              <Form.Item name="stage" noStyle>
                <SettingsSegment
                  ariaLabel="Stage"
                  options={[
                    { value: 'staging', text: 'Staging' },
                    { value: 'production', text: 'Production' },
                    { value: 'archived', text: 'Archived' },
                  ]}
                />
              </Form.Item>
            </SettingsField>
          </SettingsPanel>
          </SettingsBlock>
          </div>

          <div className="add-assistant-form-footer">
            <Space className="add-assistant-form-footer-actions">
              <Button onClick={() => navigate('/assistants')}>Cancel</Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={isLoading}
                disabled={!isEditMode && selectedExecutionType === 'cloud' && !cloudWarningAccepted}
              >
                {isEditMode ? 'Save changes' : 'Create Assistant'}
              </Button>
            </Space>
          </div>
          </div>
          </div>
        </Form>
        </Spin>
      </div>

      <Modal
        title={<Space><KeyOutlined />Add API Key for "{selectedCloudProvider?.name || ''}"</Space>}
        open={showAddProviderModal}
        onCancel={() => { setShowAddProviderModal(false); addProviderForm.resetFields(); }}
        footer={null} width={520}>
        <Alert message="Your API key will be stored and used to call the cloud provider on your behalf."
          type="info" showIcon style={{ marginBottom: 16 }} />
        <Form form={addProviderForm} layout="vertical" onFinish={handleAddProvider}>
          <Form.Item name="provider" hidden><Input /></Form.Item>
          <Form.Item label="Configuration Name" name="name" rules={[{ required: true, message: 'Required' }]}>
            <Input placeholder={`e.g., My ${selectedCloudProvider?.name || ''} Key`} />
          </Form.Item>
          <Form.Item label="API Key" name="api_key" rules={[{ required: true, message: 'Required' }]}>
            <Input.Password placeholder="sk-..." />
          </Form.Item>
          {selectedCloudProvider?.supports_custom_base && (
            <Form.Item label="Custom Base URL (Optional)" name="api_base"
              extra="Only for custom deployments (e.g., Azure endpoint)">
              <Input placeholder="https://your-deployment.openai.azure.com/" />
            </Form.Item>
          )}
          <Form.Item>
            <Button type="primary" htmlType="submit" loading={addingProvider} block>Save API Key</Button>
          </Form.Item>
        </Form>
      </Modal>

      <CreateCollectionModal
        open={createCollectionOpen}
        onCancel={() => setCreateCollectionOpen(false)}
        onCreate={handleCollectionModalCreate}
      />
    </div>
  );
};

export default AddModel;
